import { useState, useEffect } from 'react';
import { machineryApi, fuelLogsApi, maintenanceApi, stockApi, prestartsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Truck, Fuel, Wrench, ClipboardCheck, Package, Trash2 } from 'lucide-react';

export function VehiclesPage() {
  const [vehicles, setVehicles] = useState([]);
  const [fuelLogs, setFuelLogs] = useState([]);
  const [services, setServices] = useState([]);
  const [prestarts, setPrestarts] = useState([]);
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vehicles');
  
  // Dialog states
  const [vehicleDialog, setVehicleDialog] = useState(false);
  const [fuelDialog, setFuelDialog] = useState(false);
  const [serviceDialog, setServiceDialog] = useState(false);
  const [prestartDialog, setPrestartDialog] = useState(false);
  const [stockDialog, setStockDialog] = useState(false);

  useEffect(() => {
    loadAllData();
  }, []);

  const loadAllData = async () => {
    try {
      const [vehiclesRes, fuelRes, servicesRes, prestartsRes, stockRes] = await Promise.all([
        machineryApi.getAll(),
        fuelLogsApi.getAll(),
        maintenanceApi.getAll(),
        prestartsApi.getAll(),
        stockApi.getAll(),
      ]);
      setVehicles(vehiclesRes.data);
      setFuelLogs(fuelRes.data);
      setServices(servicesRes.data);
      setPrestarts(prestartsRes.data);
      setStock(stockRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Vehicle handlers
  const handleAddVehicle = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await machineryApi.create({
        name: form.get('name'),
        type: form.get('type'),
        model: form.get('model'),
        serial_number: form.get('serial_number') || null,
        purchase_date: form.get('purchase_date') || null,
        status: form.get('status'),
        notes: form.get('notes') || null,
      });
      toast.success('Vehicle added');
      setVehicleDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to add vehicle');
    }
  };

  const handleDeleteVehicle = async (id) => {
    if (!window.confirm('Delete this vehicle?')) return;
    try {
      await machineryApi.delete(id);
      toast.success('Vehicle deleted');
      loadAllData();
    } catch (err) {
      toast.error('Failed to delete vehicle');
    }
  };

  // Fuel handlers
  const handleAddFuel = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await fuelLogsApi.create({
        machinery_id: form.get('machinery_id'),
        liters: parseFloat(form.get('liters')),
        cost: parseFloat(form.get('cost')),
        date: form.get('date'),
        notes: form.get('notes') || null,
      });
      toast.success('Fuel log added');
      setFuelDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to add fuel log');
    }
  };

  // Service handlers
  const handleAddService = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await maintenanceApi.create({
        machinery_id: form.get('machinery_id'),
        type: form.get('type'),
        description: form.get('description'),
        date: form.get('date'),
        cost: parseFloat(form.get('cost') || 0),
        next_due_date: form.get('next_due_date') || null,
      });
      toast.success('Service record added');
      setServiceDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to add service record');
    }
  };

  // Prestart handlers
  const handleAddPrestart = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await prestartsApi.create({
        machinery_id: form.get('machinery_id'),
        date: form.get('date'),
        operator_name: form.get('operator_name'),
        hours_reading: parseFloat(form.get('hours_reading')) || null,
        fuel_level: form.get('fuel_level') || null,
        oil_checked: form.get('oil_checked') === 'on',
        water_checked: form.get('water_checked') === 'on',
        tyres_checked: form.get('tyres_checked') === 'on',
        lights_checked: form.get('lights_checked') === 'on',
        safety_equipment: form.get('safety_equipment') === 'on',
        damage_noted: form.get('damage_noted') || null,
        notes: form.get('notes') || null,
      });
      toast.success('Prestart recorded');
      setPrestartDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to record prestart');
    }
  };

  // Stock handlers
  const handleAddStock = async (e) => {
    e.preventDefault();
    const form = new FormData(e.target);
    try {
      await stockApi.create({
        name: form.get('name'),
        category: form.get('category'),
        quantity: parseFloat(form.get('quantity')),
        unit: form.get('unit'),
        min_quantity: parseFloat(form.get('min_quantity') || 0),
        notes: form.get('notes') || null,
      });
      toast.success('Stock item added');
      setStockDialog(false);
      loadAllData();
    } catch (err) {
      toast.error('Failed to add stock item');
    }
  };

  const handleDeleteStock = async (id) => {
    if (!window.confirm('Delete this stock item?')) return;
    try {
      await stockApi.delete(id);
      toast.success('Stock item deleted');
      loadAllData();
    } catch (err) {
      toast.error('Failed to delete stock item');
    }
  };

  const getAddButton = () => {
    switch (activeTab) {
      case 'vehicles':
        return <Button className="btn-primary" onClick={() => setVehicleDialog(true)} data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-2" /> Add Vehicle</Button>;
      case 'fuel':
        return <Button className="btn-primary" onClick={() => setFuelDialog(true)} data-testid="add-fuel-btn"><Plus className="h-4 w-4 mr-2" /> Add Fuel</Button>;
      case 'services':
        return <Button className="btn-primary" onClick={() => setServiceDialog(true)} data-testid="add-service-btn"><Plus className="h-4 w-4 mr-2" /> Add Service</Button>;
      case 'prestarts':
        return <Button className="btn-primary" onClick={() => setPrestartDialog(true)} data-testid="add-prestart-btn"><Plus className="h-4 w-4 mr-2" /> Add Prestart</Button>;
      case 'stock':
        return <Button className="btn-primary" onClick={() => setStockDialog(true)} data-testid="add-stock-btn"><Plus className="h-4 w-4 mr-2" /> Add Item</Button>;
      default:
        return null;
    }
  };

  if (loading) {
    return <div className="animate-pulse h-96 bg-muted rounded-md" data-testid="vehicles-loading" />;
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="vehicles-page">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground">Vehicles & Inventory</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="w-full justify-start bg-secondary/50 p-1">
          <TabsTrigger value="vehicles" className="flex items-center gap-2" data-testid="vehicles-tab">
            <Truck className="h-4 w-4" /> Vehicles
          </TabsTrigger>
          <TabsTrigger value="fuel" className="flex items-center gap-2" data-testid="fuel-tab">
            <Fuel className="h-4 w-4" /> Fuel
          </TabsTrigger>
          <TabsTrigger value="services" className="flex items-center gap-2" data-testid="services-tab">
            <Wrench className="h-4 w-4" /> Services
          </TabsTrigger>
          <TabsTrigger value="prestarts" className="flex items-center gap-2" data-testid="prestarts-tab">
            <ClipboardCheck className="h-4 w-4" /> Prestarts
          </TabsTrigger>
          <TabsTrigger value="stock" className="flex items-center gap-2" data-testid="stock-tab">
            <Package className="h-4 w-4" /> Stock
          </TabsTrigger>
        </TabsList>

        <div className="flex justify-end mt-4">
          {getAddButton()}
        </div>

        {/* Vehicles Tab */}
        <TabsContent value="vehicles">
          <Card>
            <CardContent className="pt-6">
              {vehicles.length === 0 ? (
                <div className="text-center py-12">
                  <Truck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No vehicles yet</p>
                  <p className="text-muted-foreground">Add your farm vehicles to track fuel, services and prestarts</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Model</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Notes</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {vehicles.map((v) => (
                      <TableRow key={v.id} className="table-row-hover">
                        <TableCell className="font-medium">{v.name}</TableCell>
                        <TableCell>{v.type}</TableCell>
                        <TableCell>{v.model}</TableCell>
                        <TableCell>
                          <Badge className={`status-${v.status}`}>{v.status.replace('_', ' ')}</Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground max-w-[200px] truncate">{v.notes || '-'}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteVehicle(v.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Fuel Tab */}
        <TabsContent value="fuel">
          <Card>
            <CardContent className="pt-6">
              {fuelLogs.length === 0 ? (
                <div className="text-center py-12">
                  <Fuel className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No fuel logs</p>
                  <p className="text-muted-foreground">Track fuel usage for your vehicles</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Liters</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Notes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {fuelLogs.map((log) => (
                      <TableRow key={log.id} className="table-row-hover">
                        <TableCell>{log.date}</TableCell>
                        <TableCell>{log.machinery_name}</TableCell>
                        <TableCell className="font-mono">{log.liters}L</TableCell>
                        <TableCell className="font-mono">${log.cost.toFixed(2)}</TableCell>
                        <TableCell className="text-muted-foreground">{log.notes || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Services Tab */}
        <TabsContent value="services">
          <Card>
            <CardContent className="pt-6">
              {services.length === 0 ? (
                <div className="text-center py-12">
                  <Wrench className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No service records</p>
                  <p className="text-muted-foreground">Track maintenance and repairs for your vehicles</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Next Due</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {services.map((svc) => (
                      <TableRow key={svc.id} className="table-row-hover">
                        <TableCell>{svc.date}</TableCell>
                        <TableCell>{svc.machinery_name}</TableCell>
                        <TableCell>{svc.type}</TableCell>
                        <TableCell>{svc.description}</TableCell>
                        <TableCell className="font-mono">${svc.cost.toFixed(2)}</TableCell>
                        <TableCell>{svc.next_due_date || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Prestarts Tab */}
        <TabsContent value="prestarts">
          <Card>
            <CardContent className="pt-6">
              {prestarts.length === 0 ? (
                <div className="text-center py-12">
                  <ClipboardCheck className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No prestart checks</p>
                  <p className="text-muted-foreground">Record daily prestart checks for your vehicles</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Vehicle</TableHead>
                      <TableHead>Operator</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Fuel</TableHead>
                      <TableHead>Checks</TableHead>
                      <TableHead>Damage</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {prestarts.map((ps) => (
                      <TableRow key={ps.id} className="table-row-hover">
                        <TableCell>{ps.date}</TableCell>
                        <TableCell>{ps.machinery_name}</TableCell>
                        <TableCell>{ps.operator_name}</TableCell>
                        <TableCell>{ps.hours_reading || '-'}</TableCell>
                        <TableCell>{ps.fuel_level || '-'}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {ps.oil_checked && <Badge variant="outline" className="text-xs">Oil</Badge>}
                            {ps.water_checked && <Badge variant="outline" className="text-xs">Water</Badge>}
                            {ps.tyres_checked && <Badge variant="outline" className="text-xs">Tyres</Badge>}
                            {ps.lights_checked && <Badge variant="outline" className="text-xs">Lights</Badge>}
                          </div>
                        </TableCell>
                        <TableCell className="text-destructive">{ps.damage_noted || '-'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock">
          <Card>
            <CardContent className="pt-6">
              {stock.length === 0 ? (
                <div className="text-center py-12">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-lg font-medium">No stock items</p>
                  <p className="text-muted-foreground">Track your inventory and supplies</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Quantity</TableHead>
                      <TableHead>Min. Qty</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stock.map((item) => (
                      <TableRow key={item.id} className="table-row-hover">
                        <TableCell className="font-medium">{item.name}</TableCell>
                        <TableCell>{item.category}</TableCell>
                        <TableCell className="font-mono">{item.quantity} {item.unit}</TableCell>
                        <TableCell className="font-mono">{item.min_quantity} {item.unit}</TableCell>
                        <TableCell>
                          {item.quantity <= item.min_quantity ? (
                            <Badge variant="destructive">Low Stock</Badge>
                          ) : (
                            <Badge className="bg-green-100 text-green-800">In Stock</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteStock(item.id)}>
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Vehicle Dialog */}
      <Dialog open={vehicleDialog} onOpenChange={setVehicleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Vehicle</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddVehicle} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input name="name" placeholder="e.g., John Deere 1" required />
              </div>
              <div className="space-y-2">
                <Label>Type *</Label>
                <Input name="type" placeholder="Tractor, Mower..." required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Model *</Label>
                <Input name="model" required />
              </div>
              <div className="space-y-2">
                <Label>Serial Number</Label>
                <Input name="serial_number" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Purchase Date</Label>
                <Input name="purchase_date" type="date" />
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select name="status" defaultValue="operational">
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operational">Operational</SelectItem>
                    <SelectItem value="maintenance">Under Maintenance</SelectItem>
                    <SelectItem value="out_of_service">Out of Service</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea name="notes" />
            </div>
            <Button type="submit" className="w-full btn-primary">Add Vehicle</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Fuel Dialog */}
      <Dialog open={fuelDialog} onOpenChange={setFuelDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Fuel Log</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddFuel} className="space-y-4">
            <div className="space-y-2">
              <Label>Vehicle *</Label>
              <Select name="machinery_id" required>
                <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Liters *</Label>
                <Input name="liters" type="number" step="0.01" required />
              </div>
              <div className="space-y-2">
                <Label>Cost ($) *</Label>
                <Input name="cost" type="number" step="0.01" required />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea name="notes" />
            </div>
            <Button type="submit" className="w-full btn-primary">Add Fuel Log</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Service Dialog */}
      <Dialog open={serviceDialog} onOpenChange={setServiceDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Service Record</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddService} className="space-y-4">
            <div className="space-y-2">
              <Label>Vehicle *</Label>
              <Select name="machinery_id" required>
                <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                <SelectContent>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Service Type *</Label>
                <Input name="type" placeholder="Oil Change, Repair..." required />
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea name="description" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cost ($)</Label>
                <Input name="cost" type="number" step="0.01" />
              </div>
              <div className="space-y-2">
                <Label>Next Due Date</Label>
                <Input name="next_due_date" type="date" />
              </div>
            </div>
            <Button type="submit" className="w-full btn-primary">Add Service</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Prestart Dialog */}
      <Dialog open={prestartDialog} onOpenChange={setPrestartDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Prestart Check</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddPrestart} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Vehicle *</Label>
                <Select name="machinery_id" required>
                  <SelectTrigger><SelectValue placeholder="Select vehicle" /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Date *</Label>
                <Input name="date" type="date" required defaultValue={new Date().toISOString().split('T')[0]} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Operator Name *</Label>
                <Input name="operator_name" required />
              </div>
              <div className="space-y-2">
                <Label>Hours Reading</Label>
                <Input name="hours_reading" type="number" step="0.1" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Fuel Level</Label>
              <Select name="fuel_level">
                <SelectTrigger><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full</SelectItem>
                  <SelectItem value="3/4">3/4</SelectItem>
                  <SelectItem value="1/2">1/2</SelectItem>
                  <SelectItem value="1/4">1/4</SelectItem>
                  <SelectItem value="empty">Empty</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-3">
              <Label>Checks Completed</Label>
              <div className="grid grid-cols-2 gap-3">
                <label className="flex items-center gap-2">
                  <Checkbox name="oil_checked" />
                  <span className="text-sm">Oil Checked</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox name="water_checked" />
                  <span className="text-sm">Water Checked</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox name="tyres_checked" />
                  <span className="text-sm">Tyres Checked</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox name="lights_checked" />
                  <span className="text-sm">Lights Checked</span>
                </label>
                <label className="flex items-center gap-2">
                  <Checkbox name="safety_equipment" />
                  <span className="text-sm">Safety Equipment</span>
                </label>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Damage Noted</Label>
              <Input name="damage_noted" placeholder="Any damage or issues..." />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea name="notes" />
            </div>
            <Button type="submit" className="w-full btn-primary">Submit Prestart</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add Stock Dialog */}
      <Dialog open={stockDialog} onOpenChange={setStockDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Add Stock Item</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddStock} className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input name="name" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category *</Label>
                <Input name="category" placeholder="Chemicals, Parts..." required />
              </div>
              <div className="space-y-2">
                <Label>Unit *</Label>
                <Input name="unit" placeholder="L, kg, pcs..." required />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantity *</Label>
                <Input name="quantity" type="number" step="0.01" required />
              </div>
              <div className="space-y-2">
                <Label>Min. Quantity</Label>
                <Input name="min_quantity" type="number" step="0.01" defaultValue="0" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea name="notes" />
            </div>
            <Button type="submit" className="w-full btn-primary">Add Item</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
